import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { type FC } from "react";
import { IconCode } from "@/assets/icons/geist/IconCode";
import { IconBook } from "@/assets/icons/geist/IconBook";
import { Tabs, TabsList, TabsTrigger } from "@/components/podkit/tabs/Tabs";
import { Tooltip } from "@/components/Tooltip";

export type EnvironmentViewMode = "details" | "vscode";

export type ViewModeToggleProps = PropsWithClassName & {
    canViewVSCodeBrowser: boolean;
    value: EnvironmentViewMode;
    onValueChange: (value: EnvironmentViewMode) => void;
    "data-testid"?: string;
};

export const ViewModeToggle: FC<ViewModeToggleProps> = ({
    canViewVSCodeBrowser,
    value,
    onValueChange,
    className,
    "data-testid": dataTestId,
}) => {
    return (
        <Tabs
            value={value}
            onValueChange={(newValue: string) => {
                onValueChange(newValue as EnvironmentViewMode);
            }}
            defaultValue="details"
            className={cn("w-full", className)}
            data-testid={dataTestId}
        >
            <TabsList className="w-fit flex-row">
                <TabsTrigger
                    value="details"
                    aria-label="Details view"
                    LeadingIcon={IconBook}
                    className="size-7 gap-0 @xs:size-auto @xs:gap-2"
                >
                    <span className="sr-only truncate @xs:not-sr-only">Environment</span>
                </TabsTrigger>

                <Tooltip
                    content={canViewVSCodeBrowser ? "VS Code Browser view" : "Start environment to view code"}
                    usePortal
                >
                    {/* We need this div wrapper as the Tooltip otherwise overwrites the data-state attribute of the TabsTrigger */}
                    <div className="contents">
                        <TabsTrigger
                            disabled={!canViewVSCodeBrowser}
                            value="vscode"
                            aria-label="VS Code Browser view"
                            LeadingIcon={IconCode}
                            className="size-7 gap-0 @xs:size-auto @xs:gap-2"
                        >
                            <span className="sr-only truncate @xs:not-sr-only">Code</span>
                        </TabsTrigger>
                    </div>
                </Tooltip>
            </TabsList>
        </Tabs>
    );
};
